<template>
  <PrimeButton
    :label="props.label"
    :pt="passThrough"
    :class="[sizeClass, typeClass, noLabelClass, loadingClass]"
    :unstyled="true"
    :disabled="props.disabled"
    :loading="props.loading"
    class="uppercase transition-all duration-200 cursor-pointer inline-flex items-center justify-center whitespace-nowrap"
    @click="(e) => emit('click', e)"
  >
    <slot v-if="$slots.default" />
    <template v-slot:icon>
      <span v-if="props.icon || $slots.customIcon" class="icon-size flex" :class="iconOrderClass">
        <slot v-if="$slots.customIcon" name="customIcon" />
        <MeridiaIcon
          v-else-if="props.icon"
          :name="props.icon"
          type="material-icons-outlined"
          :class="getPtClass < PassThrough<ButtonPassThroughOptions> > ('icon', passThrough)"
          style="font-size: inherit"
        />
      </span>
    </template>
  </PrimeButton>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  ButtonType,
  ButtonSize,
  ButtonIconPosition,
  ButtonProps,
} from './types';
import { ButtonPassThroughOptions } from 'primevue';
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import PrimeButton from 'primevue/button';
import { getPtClass } from '../utils';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

const props = defineProps<ButtonProps>();

defineSlots<{
  default: () => any;
  customIcon: () => any;
}>();

const emit = defineEmits<{
  click: [e: MouseEvent];
}>();

const sizeClass = computed(() => {
  return {
    'meridia-button-mini': props.size === ButtonSize.MINI,
    'meridia-button-base': props.size === ButtonSize.BASE,
    'meridia-button-medium': props.size === ButtonSize.MEDIUM,
    'meridia-button-large': props.size === ButtonSize.LARGE,
  };
});

const noLabelClass = computed(() => {
  return {
    'meridia-button-no-label': !props.label,
  };
});

const loadingClass = computed(() => {
  return {
    'cursor-wait': props.loading,
  };
});

const iconOrderClass = computed(() => {
  return props.icon_position === ButtonIconPosition.RIGHT ? 'icon-right' : '';
});

const typeClass = computed(() => {
  switch (props.type) {
    case ButtonType.PRIMARY:
      return 'meridia-button-primary';
    case ButtonType.SECONDARY:
      return 'meridia-button-secondary';
    case ButtonType.TERTIARY:
      return 'meridia-button-tertiary';
    case ButtonType.DESTRUCTIVE:
      return 'meridia-button-destructive';
    case ButtonType.NEUTRAL:
      return 'meridia-button-neutral';
    case ButtonType.QUATERNARY:
      return 'meridia-button-quaternary';
    case ButtonType.DESTRUCTIVE_TERTIARY:
      return 'meridia-button-destructive-tertiary';
    default:
      return '';
  }
});

const passThrough = usePassThrough(
  props.pt,
  {
    label: () => ({
      class: {
        hidden: !props.label,
      },
    }),
  } as ButtonPassThroughOptions,
  { mergeSections: false, mergeProps: true }
) as PassThrough<ButtonPassThroughOptions>;
</script>

<style lang="scss" scoped>
.icon-right {
  @apply order-1;
}

.meridia-button-mini {
  @apply py-1.5 px-m radius-small text-caption-small gap-3xs;
  &.meridia-button-no-label {
    @apply p-2xs gap-null;
  }
  .icon-size {
    @apply icon-xs;
  }
}
.meridia-button-base {
  @apply py-xs px-l radius-full text-caption-base gap-2xs;
  &.meridia-button-no-label {
    @apply p-xs gap-null;
  }
  .icon-size {
    @apply icon-s;
  }
}
.meridia-button-medium {
  @apply py-s px-xl radius-full text-caption-base gap-2xs;
  &.meridia-button-no-label {
    @apply p-s gap-null;
  }
  .icon-size {
    @apply icon-s;
  }
}
.meridia-button-large {
  @apply py-m px-2xl radius-full text-caption-large gap-xs;
  &.meridia-button-no-label {
    @apply p-m gap-null;
  }
  .icon-size {
    @apply icon-m;
  }
}

.meridia-button-primary {
  &.meridia-button-mini {
    @apply bg-neutral-surface-elevated text-neutral-text-negative-title;
    &:hover {
      @apply bg-neutral-surface-strong text-neutral-text-negative-title;
    }
    &:active {
      @apply bg-neutral-surface-elevated text-neutral-text-caption;
    }
    &:disabled {
      @apply cursor-not-allowed bg-neutral-surface-disabled text-neutral-text-disabled;
    }
  }

  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply bg-primary-surface-default text-primary-label-elevated;
    &:hover {
      @apply bg-primary-surface-lighter text-primary-label-elevated;
    }
    &:active {
      @apply bg-primary-surface-darker text-primary-label-elevated;
    }
    &:disabled {
      @apply cursor-not-allowed bg-neutral-surface-disabled text-neutral-text-disabled;
    }
  }
}
.meridia-button-secondary {
  &.meridia-button-mini,
  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply border border-primary-border-default bg-neutral-surface-default text-primary-label-default;
    &:hover {
      @apply border border-primary-border-default bg-primary-surface-muted text-primary-label-default;
    }
    &:active {
      @apply border border-primary-border-darker bg-primary-surface-muted text-primary-label-darker;
    }
    &:disabled {
      @apply border cursor-not-allowed border-neutral-border-disabled bg-neutral-surface-default text-neutral-text-disabled;
    }
  }
}
.meridia-button-tertiary {
  &.meridia-button-mini {
    @apply bg-transparent text-neutral-text-elevated;
    &:hover {
      @apply bg-black/[0.03] text-neutral-text-subtitle;
    }
    &:active {
      @apply bg-black/5 text-neutral-text-elevated;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-disabled;
    }
  }

  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply bg-transparent text-primary-label-default;
    &:hover {
      @apply bg-transparent text-primary-label-default;
    }
    &:active {
      @apply bg-transparent text-primary-label-darker;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-disabled;
    }
  }
}
.meridia-button-quaternary {
  &.meridia-button-mini {
    @apply bg-transparent text-primary-label-default;
    &:hover {
      @apply bg-primary-surface-default/10 text-primary-label-default;
    }
    &:active {
      @apply bg-primary-surface-default/[0.08] text-primary-label-darker;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-negative-caption;
    }
  }

  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply bg-transparent text-primary-label-default;
    &:hover {
      @apply bg-transparent text-primary-label-default;
    }
    &:active {
      @apply bg-transparent text-primary-label-darker;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-disabled;
    }
  }
}
.meridia-button-destructive {
  &.meridia-button-mini,
  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply bg-critical-surface-default text-neutral-text-negative-title;
    &:hover {
      @apply bg-critical-surface-lighter text-neutral-text-negative-title;
    }
    &:active {
      @apply bg-critical-surface-darker text-neutral-text-negative-title;
    }
    &:disabled {
      @apply cursor-not-allowed bg-critical-surface-disabled text-critical-label-disabled;
    }
  }
}
.meridia-button-destructive-tertiary {
  &.meridia-button-mini {
    @apply bg-transparent text-critical-label-default;
    &:hover {
      @apply bg-black/[0.03] text-critical-label-darker;
    }
    &:active {
      @apply bg-black/5 text-critical-label-darker;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-disabled;
    }
  }

  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply bg-transparent text-critical-label-default;
    &:hover {
      @apply bg-transparent text-critical-label-darker;
    }
    &:active {
      @apply bg-transparent text-critical-label-darker;
    }
    &:disabled {
      @apply cursor-not-allowed bg-transparent text-neutral-text-disabled;
    }
  }
}
.meridia-button-neutral {
  &.meridia-button-mini,
  &.meridia-button-base,
  &.meridia-button-medium,
  &.meridia-button-large {
    @apply border border-neutral-border-darker bg-neutral-surface-default text-neutral-text-body;
    &:hover {
      @apply border border-neutral-border-darker bg-neutral-surface-muted text-neutral-text-body;
    }
    &:active {
      @apply border border-neutral-border-elevated bg-neutral-surface-muted text-neutral-text-body;
    }
    &:disabled {
      @apply border cursor-not-allowed border-neutral-border-disabled bg-neutral-surface-default text-neutral-text-disabled;
    }
  }
}
</style>
